import { PropsWithChildren, useState } from "react";
import { Spinner } from "@radix-ui/themes";
import { cn } from "@/core/helpers/cn";
import formatPrice from "@/core/helpers/formatPrice";
import { usePlayerQuery } from "@/shared/hooks/useGetPlayer";
import { SwitchIcon } from "@/shared/icons";
import Button from "@/shared/ui/button/Button";
import { BankErrors } from "@/domains/bank/constants";
import toast from "@/domains/toasts/helpers/toast";
import { useBankCharacterQuery } from "@/domains/bank/hooks/useBankCharacterQuery";
import { useBankTariffsQuery } from "@/domains/bank/hooks/useBankTariffs";
import { useChangeTariffMutation } from "@/domains/bank/hooks/useChangeTariffMutation";
import { BankBranchDrawer } from "@/domains/bank/components/BankBranch/components/BankBranchDrawer";
import { LimitSlider } from "@/domains/bank/components/BankBranch/components/LimitSlider";
import { BankCard } from "@/domains/bank/components/BankCard/BankCard";
import { BankTariffSelect } from "@/domains/bank/components/BankTariffSelect";

function Title({ children, className }: PropsWithChildren<{ className?: string }>) {
  return (
    <div className={cn("font-bold leading-none uppercase text-white/50 !text-12", className)}>
      {children}
    </div>
  );
}

export function BankBranchCardInfoContainer() {
  const playerQuery = usePlayerQuery();
  const characterQuery = useBankCharacterQuery();
  const [isChangeTariffOpen, setIsChangeTariffOpen] = useState(false);
  const tariffsQuery = useBankTariffsQuery({
    enabled: isChangeTariffOpen,
  });
  const changeTariffMutation = useChangeTariffMutation();
  const currentTariffId = Number(characterQuery.data?.tariff.id);

  const [selectedTariffId, setSelectedTariffId] = useState<number>(currentTariffId);

  if (characterQuery.isLoading || tariffsQuery.isLoading) {
    return <Spinner className="size-5 animate-spin" />;
  }

  const { accountNumber, tariff, limit } = characterQuery.data;
  const tariffs = tariffsQuery.data?.tariffs ?? [];
  const ownerName = playerQuery.data?.character?.name.content;
  const isCurrentTariffSelected = selectedTariffId === currentTariffId;

  return (
    <div className="flex flex-col justify-between">
      <BankCard.Template tier={tariff.cardStyleAsset} name={tariff.name} isInteractive>
        <BankCard.CardInfo number={accountNumber} name={ownerName} />
      </BankCard.Template>

      <div className="flex flex-col gap-4">
        <Title>Лимиты по текущему тарифу</Title>

        <div className="bg-dark-800 rounded-[12px] p-6">
          <div>
            <div className="text-white/70 font-normal text-15">
              Бесплатные переводы внутри банка
            </div>
            <div className="mb-2 text-15">
              {formatPrice(tariff.transferLimit)} ₽ <span className="text-white/70">/ месяц</span>
            </div>

            <LimitSlider min={0} max={tariff.transferLimit} value={limit.transferUsedAmount} />
          </div>

          <hr className="border-white/3 my-5" />

          <div>
            <div className="text-white/70 font-normal text-15">Бесплатный вывод наличных</div>
            <div className="mb-2 text-15">
              {formatPrice(tariff.withdrawalLimit)} ₽ <span className="text-white/70">/ месяц</span>
            </div>

            <LimitSlider min={0} max={tariff.withdrawalLimit} value={limit.withdrawalUsedAmount} />
          </div>
        </div>
      </div>

      <Button
        variant="solid-gradient"
        data-mz-accent="green"
        className="w-full text-white h-[44px] flex items-center gap-2"
        onClick={() => setIsChangeTariffOpen(true)}
      >
        <SwitchIcon className="size-5" />
        Сменить тариф
      </Button>

      <BankBranchDrawer
        title="Смена тарифного плана"
        isOpen={isChangeTariffOpen}
        onClose={() => setIsChangeTariffOpen(false)}
        size="lg"
      >
        {tariffsQuery.isLoading ? (
          <Spinner className="size-5 animate-spin" />
        ) : (
          <div className="flex flex-col gap-3 h-full">
            <div className="h-[240px] pr-8">
              <BankTariffSelect
                tariffs={tariffs}
                value={selectedTariffId}
                onChange={setSelectedTariffId}
              />
            </div>

            <p className="text-white/30 text-15">
              * После выбора тарифного плана вам нужно будет оплатить стоимость перехода на новый
              тариф.
            </p>

            <p className="text-white/30 text-15">
              <span className="text-white/50">Номер банковского счета останется прежним</span>,
              изменится только стоимость обслуживания тарифного плана и&nbsp;его лимиты, исходя из
              вашего выбора.
            </p>

            <div className="mt-auto mb-2">
              {isCurrentTariffSelected ? (
                <div className="text-white/50 text-15 text-medium">
                  Это ваш текущий тарифный план
                </div>
              ) : (
                <>
                  <div className="text-sbank text-20 font-bold tabular-nums">
                    {formatPrice(tariffs.find((t) => t.id === selectedTariffId)?.price)} ₽
                  </div>

                  <div className="text-white/50 text-15 text-medium">
                    Стоимость смены тарифного плана
                  </div>
                </>
              )}
            </div>

            <footer className="grid grid-cols-2 gap-2 h-[44px]">
              <Button
                variant="solid-gradient"
                data-mz-accent="green"
                disabled={changeTariffMutation.isPending || isCurrentTariffSelected}
                onClick={() => {
                  changeTariffMutation.mutate(selectedTariffId, {
                    onSuccess: () => {
                      toast.success("Вы сменили тариф обслуживания!");
                      setIsChangeTariffOpen(false);
                    },
                    onError: (error) => {
                      if (error.message === BankErrors.NOT_ENOUGH_MONEY_ERROR) {
                        toast.error("Недостаточно средств на счёте для оплаты тарифа!");
                      }
                    },
                  });
                }}
              >
                {changeTariffMutation.isPending ? (
                  <Spinner className="size-5 animate-spin" />
                ) : (
                  <div className="flex items-center gap-2">
                    <SwitchIcon className="size-5" /> Подтвердить смену
                  </div>
                )}
              </Button>

              <Button
                variant="solid-outline"
                data-mz-accent="green"
                className="bg-[#21a0381a] text-sbank border-none hover:bg-[#21a0381a]/12"
                onClick={() => setIsChangeTariffOpen(false)}
              >
                Отмена
              </Button>
            </footer>
          </div>
        )}
      </BankBranchDrawer>
    </div>
  );
}
